import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ContactHub from "@/components/ContactHub";
import StickyCTA from "@/components/StickyCTA";
import CookieConsent from "@/components/CookieConsent";
import PageTransition from "@/components/PageTransition";
import Home from "@/pages/Home";
import { LanguageProvider } from "@/i18n";
import { AdminAuthProvider } from "@/admin/auth";
import { prefetchRoles } from "@/data/rolesCache";

// Code splitting: only the homepage ships in the initial bundle.
const Services = lazy(() => import("@/pages/Services"));
const Workforce = lazy(() => import("@/pages/Workforce"));
const Industries = lazy(() => import("@/pages/Industries"));
const HSE = lazy(() => import("@/pages/HSE"));
const ForEmployers = lazy(() => import("@/pages/ForEmployers"));
const ForCandidates = lazy(() => import("@/pages/ForCandidates"));
const OpenRoles = lazy(() => import("@/pages/OpenRoles"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const About = lazy(() => import("@/pages/About"));
const Market = lazy(() => import("@/pages/Market"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.Privacy })));
const Terms = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.Terms })));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));

const PageLoader = () => (
  <div className="flex min-h-[60vh] items-center justify-center" data-testid="page-loader" aria-busy="true">
    <span className="h-8 w-8 animate-spin border-2 border-[#C8D3DB] border-t-[#0F5C8C]" />
  </div>
);

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const id = setTimeout(() => {
      if (hash) document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      else window.scrollTo({ top: 0, behavior: "instant" });
    }, 200);
    return () => clearTimeout(id);
  }, [pathname, hash]);
  return null;
};

const PublicSite = () => {
  const location = useLocation();
  return (
  <>
    <Header />
    <main className="glass-page">
      <PageTransition>
      <Suspense fallback={<PageLoader />}>
      <Routes location={location}>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/workforce" element={<Workforce />} />
        <Route path="/partners" element={<Navigate to="/about#partners" replace />} />
        <Route path="/industrial-cleaning" element={<Navigate to="/workforce" replace />} />
        <Route path="/hse" element={<HSE />} />
        <Route path="/industries" element={<Industries />} />
        <Route path="/for-employers" element={<ForEmployers />} />
        <Route path="/for-candidates" element={<ForCandidates />} />
        <Route path="/open-roles" element={<OpenRoles />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/about" element={<About />} />
        <Route path="/markets" element={<Navigate to="/about#global-operations" replace />} />
        <Route path="/markets/:slug" element={<Market />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/global-operations" element={<Navigate to="/about#global-operations" replace />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
      </Routes>
      </Suspense>
      </PageTransition>
    </main>
    <Footer />
    <ContactHub />
    <StickyCTA />
    <CookieConsent />
  </>
  );
};

const Shell = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  useEffect(() => { if (!isAdmin) prefetchRoles(); }, [isAdmin]);
  useEffect(() => {
    if (isAdmin) return undefined;
    const lenis = new Lenis({ lerp: 0.09 });
    let id;
    const raf = (time) => {
      lenis.raf(time);
      id = requestAnimationFrame(raf);
    };
    id = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
    };
  }, [isAdmin]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/*" element={<AdminDashboard />} />
        <Route path="*" element={<PublicSite />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <div className="App text-[#0E1B24]">
      <div className="nv-bg" aria-hidden="true" style={{ "--nv-bg-m": "url(/assets/bg-water-m.webp)", "--nv-bg-d": "url(/assets/bg-water.webp)" }} />
      <div className="nv-country-bg" aria-hidden="true" data-testid="page-country-backdrop" />
      <div className="grain-overlay" />
      <LanguageProvider>
        <AdminAuthProvider>
          <BrowserRouter>
            <ScrollManager />
            <Shell />
          </BrowserRouter>
        </AdminAuthProvider>
      </LanguageProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
